import { AyudaSec as S, AyudaCod as Cod } from './ayuda-partes.jsx'

/**
 * El contrato de la plataforma (CONTRATO-APP.md), troceado por página. Cada bloque vive donde
 * se necesita: los límites de la base de datos en el editor SQL, el patrón de ficheros en
 * Almacenamiento… El documento entero sigue en el repositorio; esto es la versión «en el sitio
 * y en el momento». Si cambia el contrato, cambia AQUÍ TAMBIÉN — son la misma verdad contada
 * dos veces, y desmentirse es peor que repetirse.
 */

export const AyudaDespliegue = () => (
  <>
    <S titulo="Cómo se despliega">
      <p>
        Un <code>git push</code> a la <strong>rama de despliegue</strong> dispara el build
        (Nixpacks) y publica en <code>&lt;proyecto&gt;.apps.cloudtreesolutions.com</code>. No hay
        botones que recordar: el repositorio es la única fuente.
      </p>
      <p>
        Excepción a propósito: un push que solo toca <code>functions/</code> <strong>no
        reconstruye la app</strong> — sincroniza las funciones en segundos.
      </p>
    </S>
    <S titulo="Lo que tu app debe cumplir">
      <p>
        Escuchar en el puerto que llega en <code>PORT</code> (3000 por defecto; configurable en el
        alta y en Ajustes) y arrancar con <code>npm start</code> o equivalente detectable por
        Nixpacks.
      </p>
      <Cod>{`# nixpacks.toml — acelera builds 10x
[phases.install]
onlyIncludeFiles = ["package.json", "package-lock.json"]`}</Cod>
      <p>
        Sin ese fichero, cada commit copia el árbol entero antes del <code>npm ci</code> y
        reinstala todas las dependencias: es la causa nº 1 de builds de minutos.
      </p>
    </S>
    <S titulo="Lo que NO hay">
      <p>
        Ni healthcheck real (el tráfico entra al arrancar el proceso), ni previews por rama, ni
        copias de seguridad. Es un entorno de desarrollo: reproducibilidad, no respaldo.
      </p>
    </S>
  </>
)

export const AyudaBaseDatos = () => (
  <>
    <S titulo="La conexión">
      <p>
        <code>DATABASE_URL</code> llega puesta como variable de entorno — nunca hay contraseña
        que copiar. En la app:
      </p>
      <Cod>{`const pool = new pg.Pool({
  connectionString: process.env.DATABASE_URL,
  max: 4,   // ¡no 10!
})`}</Cod>
    </S>
    <S titulo="Los límites del rol">
      <p>
        Aplican igual a la app, a este editor y a las funciones: <strong>30 s</strong> por
        sentencia, <strong>60 s</strong> de transacción ociosa, <strong>5 s</strong> para
        conseguir un bloqueo y <strong>10 conexiones en total</strong>. Las 10 se reparten entre
        app (× réplicas), consola y funciones: un pool de 10 deja fuera al resto.
      </p>
    </S>
    <S titulo="Cambios de esquema (migraciones)">
      <p>
        Tu rol es <strong>dueño de su base</strong>: migraciones versionadas en el repositorio,
        aplicadas por la app al arrancar (Prisma, Alembic, node-pg-migrate…). Lo que hagas aquí a
        mano <strong>no queda en el repo</strong>: vale para explorar, no como fuente del esquema.
      </p>
    </S>
    <S titulo="Sin copias de seguridad">
      <p>No hay respaldo de las bases de proyecto, y no va a haberlo. No guardes ahí nada que duela perder.</p>
    </S>
  </>
)

export const AyudaFuncionesLista = () => (
  <>
    <S titulo="El convenio">
      <Cod>{`functions/<nombre>/main.<ext>   ← el código
functions/<nombre>/config.json  ← webhook y cron`}</Cod>
      <p>
        La extensión decide el lenguaje (<code>.ts</code> Bun, <code>.py</code> Python,{' '}
        <code>.sh</code> Bash, <code>.sql</code> PostgreSQL). El push publica sin reconstruir la
        app, y lo que guardas en este editor se commitea al repositorio: doble sentido.
      </p>
    </S>
    <S titulo="Llamarlas desde tu app">
      <Cod>{`POST https://consola.apps.cloudtreesolutions.com
     /api/webhook/fn/<proyecto>/<función>
x-hook-token: <el token>

→ 202 { "jobId": "..." }   ¡asíncrono!`}</Cod>
      <p>
        Responde <strong>202 con un jobId</strong>, no con el resultado. Si lo necesitas en la
        misma llamada, añade <code>/sync</code> al path: espera hasta 60 s y responde 200 con el
        resultado (504 con el jobId si no llega a tiempo). El <strong>token se enseña una sola
        vez</strong> al generarlo — guárdalo como variable de entorno de tu app.
      </p>
    </S>
    <S titulo="Programarlas">
      <p>
        En el <code>config.json</code>: <code>{'{"cron": "*/15 * * * *"}'}</code> (5 campos, como
        siempre). Un webhook nace <strong>autenticado</strong>; abrirlo es decisión explícita y la
        consola lo marca en rojo.
      </p>
    </S>
  </>
)

export const AyudaAlmacenamiento = () => (
  <>
    <S titulo="Tu bucket, tus credenciales">
      <p>
        Cada proyecto tiene su bucket (<code>proj-&lt;slug&gt;</code>) y una credencial que solo
        lo ve a él. Llega como variables <code>S3_*</code> — vale cualquier cliente S3 con dos
        ajustes obligatorios:
      </p>
      <Cod>{`endpoint:  S3_ENDPOINT (interno)
region:    S3_REGION
path-style: SÍ (forcePathStyle /
            addressing_style: "path")`}</Cod>
      <p>Sin path-style, el SDK busca <code>proj-x.minio</code>, que no existe como nombre.</p>
    </S>
    <S titulo="Ficheros generados por la app">
      <p>
        El disco del contenedor <strong>se estrena en cada despliegue</strong>. El patrón:
        generar en <code>/tmp</code> → subir a S3 → servir desde S3. Escribir en una ruta local y
        confiar en que siga ahí es el único patrón que no funciona — y no avisa.
      </p>
    </S>
    <S titulo="La cuota es dura">
      <p>
        La impone MinIO sobre el bucket: al llegar al límite las subidas fallan, también las que
        haga la app con sus credenciales. La amplía un admin de la plataforma (el engranaje).
        Subidas desde esta pestaña: hasta 50 MiB por fichero; más grande, por S3 directo.
      </p>
    </S>
  </>
)

export const AyudaEnv = () => (
  <>
    <S titulo="Cuándo surten efecto">
      <p>
        Las variables se inyectan <strong>al desplegar</strong>: guardar aquí no cambia la app en
        caliente. «Aplicar y desplegar» hace las dos cosas.
      </p>
    </S>
    <S titulo="Las de la plataforma">
      <p>
        <code>NODE_ENV</code>, <code>PORT</code>, <code>DATABASE_URL</code> y las{' '}
        <code>S3_*</code> las gestiona la consola (sección de abajo): son nombres{' '}
        <strong>reservados</strong>, y una variable tuya no puede pisarlas.
      </p>
    </S>
    <S titulo="Importar un .env">
      <p>
        Pega o arrastra el fichero: antes de aplicar se enseña qué se crea, qué se sobrescribe
        (con el valor anterior a la vista) y qué se rechaza. Los valores se guardan cifrados.
      </p>
    </S>
  </>
)
